import {
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useOktaAuth } from "@okta/okta-react";
import MfaService from "../../../MfaService";
import MfaFactor, { MfaEnrollMethod } from "../../../models/mfaFactor.model";
import toastMessages from "../../helpers/toastMessage";
import { getApiErrorMessage } from "../../../../utils/helpers/communicationPreferencesHelpers";
import {
  getEnrolledMfaMethodLabels,
  getMaskedEmailFromFactors,
  getMfaEnrollmentStatus,
  getMfaMethodLabel,
  getMfaStatusDescription,
  getMissingMfaMethod,
  getPendingActivationFactor,
  isMfaEnrolled,
} from "../../helpers/mfaHelpers";
import { IS_MFA_SOFT_LAUNCH_ENABLED } from "../../../../constants/mfaFlags";

function useAccessToken(): string | undefined {
  const { authState } = useOktaAuth();
  return authState?.accessToken?.accessToken;
}

export function useMfaFactors(): UseQueryResult<MfaFactor[]> {
  const token = useAccessToken();

  return useQuery({
    queryKey: ["mfaFactors"],
    queryFn: () => MfaService.getFactors(token!),
    enabled: IS_MFA_SOFT_LAUNCH_ENABLED && !!token,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useMfaEnrollmentState() {
  const query = useMfaFactors();
  const factors = query.data;
  const enrolled = isMfaEnrolled(factors);
  const enrollmentStatus = getMfaEnrollmentStatus(factors);
  const pendingFactor = getPendingActivationFactor(factors);
  const maskedEmail = getMaskedEmailFromFactors(factors);
  const methodLabel = getMfaMethodLabel(factors);
  const statusDescription = getMfaStatusDescription(factors);
  const enrolledMethodLabels = getEnrolledMfaMethodLabels(factors);
  const missingMfaMethod = getMissingMfaMethod(factors);

  return {
    ...query,
    factors,
    enrolled,
    enrollmentStatus,
    pendingFactor,
    hasPendingActivation: !!pendingFactor,
    maskedEmail,
    methodLabel,
    statusDescription,
    enrolledMethodLabels,
    missingMfaMethod,
  };
}

export function useEnrollMfa() {
  const token = useAccessToken();

  return useMutation({
    mutationKey: ["enrollMfa"],
    mutationFn: (method: MfaEnrollMethod) => {
      if (!token) throw new Error("Not authenticated");
      return MfaService.enrollByMethod(token, method);
    },
    onSuccess: (_data, method) => {
      if (method === "email") {
        toastMessages.success("Verification code sent to your email.");
      } else {
        toastMessages.success("Scan the QR code with your authenticator app.");
      }
    },
    onError: (error: unknown) => {
      toastMessages.error(
        getApiErrorMessage(error, "Failed to start two-step verification.")
      );
    },
  });
}

/** @deprecated Prefer useEnrollMfa */
export function useEnrollMfaEmail() {
  const enroll = useEnrollMfa();
  return {
    ...enroll,
    mutate: (
      _vars: undefined,
      options?: Parameters<typeof enroll.mutate>[1]
    ) => enroll.mutate("email", options),
    mutateAsync: () => enroll.mutateAsync("email"),
  };
}

export function useActivateMfa() {
  const token = useAccessToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["activateMfa"],
    mutationFn: ({
      factorId,
      passCode,
    }: {
      factorId: string;
      passCode: string;
    }) => {
      if (!token) throw new Error("Not authenticated");
      return MfaService.activateFactor(token, factorId, passCode);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mfaFactors"] });
      toastMessages.success("Two-step verification is on.");
    },
    onError: (error: unknown) => {
      const apiMessage = getApiErrorMessage(error, "");
      const isInvalidCode =
        !apiMessage ||
        /passcode|pass code|otp|invalid|answer|expired/i.test(apiMessage);
      toastMessages.error(
        isInvalidCode ? "Incorrect code. Try Again" : apiMessage
      );
    },
  });
}
